/**
 * All widget CSS as a single string, meant to be injected via a `<style>` element that is
 * part of the Preact tree rendered inside the closed shadow root (see `App.tsx`). A shadow
 * root's styles never leak out to the host page and the host page's styles never leak in, so
 * everything below only ever needs to consider *this* widget's own DOM — no global stylesheet,
 * no build-time CSS pipeline, and class names don't need host-page-wide uniqueness (though
 * they're still prefixed `ft-` for readability and to avoid collisions between the widget's
 * own elements).
 *
 * `Button.tsx` intentionally keeps its Task 5 approach of fully inline (JS object) styles
 * rather than moving to classes here — it's already self-contained/scoped by construction
 * (no class name, no cascade to worry about) and reworking it isn't part of this task. This
 * file covers the modal: overlay, dialog chrom, the type segmented control, and form fields.
 */
import {
  BRAND_ACCENT,
  BRAND_ACCENT_MUTED,
  BRAND_ACCENT_SUBTLE,
  BRAND_ACCENT_TEXT,
  BRAND_DANGER,
  BRAND_DISPLAY_STACK,
  BRAND_FONT_STACK,
  BRAND_HAIR,
  BRAND_INK,
  BRAND_LINK,
  BRAND_MIST,
  BRAND_MUTED,
  BRAND_REDACTION,
  BRAND_SCRIM,
} from './brand';

export const styles = `
.ft-overlay, .ft-overlay *, .ft-overlay *::before, .ft-overlay *::after {
  box-sizing: border-box;
}

.ft-overlay {
  /*
   * Surface tokens, so one dark-theme rule below can repaint the dialog
   * without a second copy of every selector. \`--ft-field\` is for control
   * edges and \`--ft-line\` for dividers only: the hairline is under 3:1 on
   * white, and WCAG 1.4.11 asks 3:1 of a control's boundary. #6b7699 is 4.5:1.
   * The header band follows the application's accent, so the dialog wears the
   * same color as the launcher that opened it.
   */
  --ft-surface: #ffffff;
  --ft-text: ${BRAND_INK};
  --ft-muted: ${BRAND_MUTED};
  --ft-line: ${BRAND_HAIR};
  --ft-field: #6b7699;
  --ft-placeholder: #646f93;
  --ft-recess: ${BRAND_MIST};
  --ft-band: var(--ft-accent, ${BRAND_ACCENT});
  --ft-band-ink: var(--ft-accent-text, ${BRAND_ACCENT_TEXT});
  --ft-error-text: ${BRAND_DANGER};
  /*
   * Scrollbars and the native parts of fields (the file input, a select's
   * popup, autofill) take their colors from \`color-scheme\`, not from these
   * tokens, and it inherits across the shadow boundary like the properties
   * reset below. Pinned to light here so a dark host page cannot darken the
   * light dialog; the dark rule turns it dark (I22677).
   */
  color-scheme: light;
  position: fixed;
  inset: 0;
  background: ${BRAND_SCRIM};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 2147483647;
  font-family: var(--ft-font, ${BRAND_FONT_STACK});
  /*
   * A closed shadow root blocks the host page's SELECTORS, but inherited CSS
   * properties still flow across the shadow boundary from the host element
   * down into this tree (inheritance isn't a selector match — it's a
   * property-resolution fallback that shadow DOM doesn't interrupt). A host
   * page that sets e.g. \`* { text-transform: uppercase; letter-spacing: 2px }\`
   * on its own elements has that same computed value inherited by \`.ft-overlay\`
   * and everything under it, unless something in the shadow tree re-asserts
   * its own value — which, before this, nothing did.
   *
   * This canNOT be a \`:host\` rule: \`:host\` styles lose to the outer document's
   * rules that target the host element itself (especially with \`!important\`,
   * which real hostile pages use), so a \`:host\` reset would simply not apply.
   * \`.ft-overlay\` sits inside the shadow tree, past that boundary, so no host
   * selector can out-specificity it.
   *
   * Deliberately NOT resetting \`line-height\`, \`color\`, or \`font-family\` here —
   * the widget sets those itself throughout this stylesheet, and a blanket
   * reset would fight its own declarations for no reason.
   */
  text-transform: none;
  letter-spacing: normal;
  word-spacing: normal;
  font-style: normal;
  font-variant: normal;
  text-indent: 0;
  text-shadow: none;
  white-space: normal;
  direction: ltr;
}

/*
 * Dark host pages (\`data-theme="dark"\` on the host's root, read by \`App\`).
 *
 * The accent is overridden too, not just the surface: an application's own
 * accent is usually a dark brand color, and a navy chip or Send button on a
 * navy dialog disappears. Sky with Gulf text is the brand's on-dark action
 * pair. The band goes one step darker than the surface so it still reads as a
 * header. Error copy turns white because Danger is about 3:1 on this navy and
 * fails at 12px; the textarea's red edge still carries the color.
 */
.ft-root[data-theme="dark"] .ft-overlay {
  --ft-surface: #0b1440;
  --ft-text: #ffffff;
  --ft-muted: rgba(255, 255, 255, 0.7);
  --ft-line: rgba(255, 255, 255, 0.14);
  --ft-field: rgba(255, 255, 255, 0.42);
  --ft-placeholder: rgba(255, 255, 255, 0.6);
  --ft-recess: rgba(255, 255, 255, 0.06);
  --ft-band: #020836;
  --ft-band-ink: #ffffff;
  --ft-error-text: #ffffff;
  --ft-accent: #1d94ff;
  --ft-accent-text: #040c4c;
  --ft-accent-hover: #5ab0ff;
  --ft-accent-muted: rgba(255, 255, 255, 0.24);
  --ft-accent-subtle: rgba(29, 148, 255, 0.18);
  --ft-link: #1d94ff;
  color-scheme: dark;
}

/*
 * A column: fixed header band, a body that scrolls, a fixed footer. Send never
 * scrolls out of reach, however long the marks list or the attachment list
 * grows. No drop shadow: the scrim already says "above" (design language,
 * components), and a shadow on top of it is the violation the old dialog had.
 */
.ft-dialog {
  position: relative;
  background: var(--ft-surface);
  color: var(--ft-text);
  width: 100%;
  /* 640, not 420: the modal now opens with a screenshot already attached, and
     a thumbnail plus a filename in a 420px column wraps the attachment chip
     onto its own line before the user has done anything. */
  max-width: 640px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: var(--ft-radius, 0px);
  padding: 0;
}

.ft-dialog-head {
  flex: none;
  padding: 16px 64px 14px 20px;
  background: var(--ft-band);
  color: var(--ft-band-ink);
}

.ft-dialog-heading {
  margin: 0;
  font-family: ${BRAND_DISPLAY_STACK};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.3;
  color: inherit;
}

.ft-dialog-sub {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: inherit;
  opacity: 0.85;
}

/* 44px, the touch-target minimum, sitting on the header band. */
.ft-close-button {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: var(--ft-radius, 0px);
  color: var(--ft-band-ink);
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.ft-close-glyph {
  width: 16px;
  height: 16px;
  display: block;
  fill: currentColor;
}

.ft-close-button:hover {
  background: rgba(255, 255, 255, 0.12);
}

.ft-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ft-dialog-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px 20px;
}

.ft-field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--ft-text);
}

.ft-input,
.ft-textarea {
  width: 100%;
  font: inherit;
  font-size: 14px;
  border: 1px solid var(--ft-field);
  border-radius: var(--ft-radius, 0px);
  padding: 8px 10px;
  color: var(--ft-text);
  background: var(--ft-surface);
}

.ft-input::placeholder,
.ft-textarea::placeholder {
  color: var(--ft-placeholder);
  opacity: 1;
}

.ft-textarea[aria-invalid="true"] {
  border-left-width: 3px;
  border-left-color: ${BRAND_DANGER};
}

.ft-input:focus,
.ft-textarea:focus,
.ft-type-option:focus-visible,
.ft-close-button:focus-visible,
.ft-submit-button:focus-visible,
.ft-attachments-add:focus-visible,
.ft-attachment-remove:focus-visible {
  outline: 2px solid var(--ft-accent, ${BRAND_ACCENT});
  outline-offset: 2px;
}

/* On the band, an accent ring would be navy on navy. */
.ft-close-button:focus-visible {
  outline-color: var(--ft-band-ink);
  outline-offset: -4px;
}

.ft-textarea {
  min-height: 96px;
  resize: vertical;
}

.ft-hint {
  margin-top: 4px;
  font-size: 12px;
  color: var(--ft-error-text);
}

.ft-type-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ft-type-option {
  flex: 1 1 auto;
  border: 1px solid var(--ft-field);
  background: var(--ft-surface);
  color: var(--ft-text);
  border-radius: var(--ft-radius, 0px);
  padding: 6px 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.ft-type-option--selected {
  background: var(--ft-accent, ${BRAND_ACCENT});
  border-color: var(--ft-accent, ${BRAND_ACCENT});
  color: var(--ft-accent-text, #ffffff);
}

.ft-submit-button {
  border: none;
  border-radius: var(--ft-radius, 0px);
  padding: 10px 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--ft-accent-text, #ffffff);
  background: var(--ft-accent, ${BRAND_ACCENT});
  cursor: pointer;
}

.ft-submit-button:disabled {
  background: var(--ft-accent-muted, ${BRAND_ACCENT_MUTED});
  cursor: not-allowed;
}

.ft-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ft-attachments-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ft-text);
}

/*
 * The native file input is visually hidden (clipped, not \`display: none\`) so it stays in the
 * accessibility tree and keyboard-operable; \`.ft-attachments-add\`, a \`<label for>\` pointing at
 * it, is the actual visible + accessible control (clicking or activating the label opens the
 * native file picker, and its text is the input's accessible name).
 */
.ft-attachments-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ft-attachments-add {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px dashed var(--ft-field);
  background: var(--ft-recess);
  color: var(--ft-text);
  border-radius: var(--ft-radius, 0px);
  padding: 8px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ft-attachments-add:hover {
  background: var(--ft-recess);
}

.ft-attachment-errors {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ft-attachments-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ft-attachment-item {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--ft-line);
  border-radius: var(--ft-radius, 0px);
  padding: 6px 8px;
  max-width: 100%;
}

.ft-attachment-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: var(--ft-radius, 0px);
  flex-shrink: 0;
}

.ft-attachment-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--ft-radius, 0px);
  background: var(--ft-accent-subtle, ${BRAND_ACCENT_SUBTLE});
  color: var(--ft-accent, ${BRAND_ACCENT});
  font-size: 10px;
  /* 600, not 700: only Poppins 400 and 600 are registered (see \`fonts.ts\`), so
     700 here asks for a weight nothing serves and the browser synthesises it. */
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
}

.ft-attachment-name {
  font-size: 12px;
  color: var(--ft-text);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ft-attachment-remove {
  border: none;
  background: transparent;
  color: var(--ft-muted);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 2px;
  border-radius: var(--ft-radius, 0px);
}

.ft-attachment-remove:hover {
  background: var(--ft-recess);
  color: ${BRAND_DANGER};
}

.ft-screenshot-trigger {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ft-screenshot-button {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--ft-field);
  background: var(--ft-recess);
  color: var(--ft-text);
  border-radius: var(--ft-radius, 0px);
  padding: 8px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ft-screenshot-button:hover:not(:disabled) {
  background: var(--ft-recess);
}

.ft-screenshot-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.ft-screenshot-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ft-screenshot-preview-heading {
  margin: 0;
  font-size: 14px;
  /* Body type, so 600 — the served Poppins bold. Unlike \`.ft-dialog-heading\`,
     which is the display face and correctly stays 700 (Montserrat 700 IS
     registered); this element inherits the body stack, where 700 would be
     synthesised. */
  font-weight: 600;
}

.ft-screenshot-instructions {
  margin: 0;
  font-size: 12px;
  color: var(--ft-muted);
}

.ft-screenshot-canvas {
  position: relative;
  width: 100%;
  border: 1px solid var(--ft-line);
  border-radius: var(--ft-radius, 0px);
  overflow: hidden;
  cursor: crosshair;
  touch-action: none;
  line-height: 0;
}

.ft-screenshot-image {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
  user-select: none;
}

.ft-screenshot-box {
  position: absolute;
  background: ${BRAND_REDACTION};
  border: 1px solid ${BRAND_INK};
  pointer-events: none;
}

.ft-screenshot-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.ft-screenshot-actions-secondary {
  display: flex;
  gap: 8px;
}

.ft-screenshot-clear-button,
.ft-screenshot-cancel-button {
  border: 1px solid var(--ft-field);
  background: var(--ft-surface);
  color: var(--ft-text);
  border-radius: var(--ft-radius, 0px);
  padding: 8px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ft-screenshot-clear-button:hover,
.ft-screenshot-cancel-button:hover {
  background: var(--ft-recess);
}

.ft-screenshot-confirm-button {
  border: none;
  border-radius: var(--ft-radius, 0px);
  padding: 8px 16px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--ft-accent-text, #ffffff);
  background: var(--ft-accent, ${BRAND_ACCENT});
  cursor: pointer;
}

.ft-screenshot-confirm-button:disabled {
  background: var(--ft-accent-muted, ${BRAND_ACCENT_MUTED});
  cursor: not-allowed;
}

/*
 * The scrolling middle of the dialog, between the header band and the footer.
 * Each field/control carries its own \`disabled\` prop while a submit is in
 * flight (a \`<fieldset disabled>\` would be the more idiomatic HTML, but jsdom
 * doesn't propagate that to descendants' \`.disabled\`, which would make it
 * untestable — see the comment in \`Modal.tsx\`).
 */
.ft-form-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px 20px;
}

.ft-choice {
  display: flex;
  flex-direction: column;
}

/*
 * Outside the scrolling fields, so Send stays on screen. The rule above it is
 * a divider, so it takes the hairline, not the control edge.
 */
.ft-form-footer {
  flex: none;
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 16px;
  border-top: 1px solid var(--ft-line);
}

/*
 * A failed send. The note stays in the form and the footer button becomes
 * Send again, so nothing the reporter wrote is hidden behind an error screen.
 */
.ft-send-error {
  flex: none;
  padding: 12px 20px 0;
}

.ft-send-error-copy {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ft-text);
}

/*
 * Diamond, not a square: the design language gives the diamond to urgency,
 * and a send that did not land is urgent. The sentence stays in body ink;
 * Danger paints the marker only.
 */
.ft-urgent {
  flex: none;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  background: ${BRAND_DANGER};
  transform: rotate(45deg);
}

.ft-submit-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
}

.ft-submit-status p {
  margin: 0;
  font-size: 14px;
  color: var(--ft-text);
}

.ft-submit-status .ft-hint {
  font-size: 13px;
}

.ft-overlay a {
  color: var(--ft-link, ${BRAND_LINK});
  text-decoration: underline;
}

.ft-overlay a:hover {
  text-decoration: none;
}

/*
 * The capture that leads the modal (I19222).
 *
 * It sits first in the form and is the widest thing in it, because by the time
 * a reporter reads a word of this dialog the screenshot already exists: it was
 * taken on the click that opened the modal. It used to render as a 32px chip
 * under the description, which asked them to write out what a picture in front
 * of them already said.
 */
.ft-capture {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ft-capture-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ft-capture-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ft-text);
}

.ft-capture-actions {
  display: flex;
  gap: 6px;
}

.ft-capture-action {
  border: 1px solid var(--ft-field);
  background: var(--ft-recess);
  color: var(--ft-text);
  border-radius: var(--ft-radius, 0px);
  padding: 4px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.ft-capture-action:hover:not(:disabled) {
  background: var(--ft-line);
}

.ft-capture-action:disabled {
  color: var(--ft-muted);
  cursor: default;
}

/* \`position: relative\` is what every marker's percentage coordinate resolves
   against, so the wrapper rather than the image is the positioned box: an
   image that has not decoded yet has no height, and a marker placed against it
   would land outside the frame until it did. */
.ft-capture-image-wrap {
  position: relative;
  border: 1px solid var(--ft-line);
  border-radius: var(--ft-radius, 0px);
  overflow: hidden;
  cursor: crosshair;
  line-height: 0;
}

.ft-capture-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: contain;
  object-position: top;
}

/*
 * A square marker, centred on the point it names.
 *
 * Square rather than round because the design language bans circles and
 * \`prohibited-values.test.ts\` enforces it, and the shape is doing real work:
 * a square marker is a STATUS marker in this vocabulary, which is what a
 * "something is wrong here" pin is.
 *
 * The translate is what makes the coordinate mean the middle of the marker
 * rather than its top-left corner, which is the difference between pointing at
 * a thing and pointing just below and right of it.
 */

.ft-capture-pin {
  position: absolute;
  transform: translate(-50%, -50%);
  min-width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  /* The accent, NOT danger. Red means something broke (ADR 0003) and a marker a
     reporter drops to say "look here" is a locator, not a status. It shipped as
     danger for one deploy, which also made the same marker red here and navy in
     the admin lightbox — one thing, two colors, on two surfaces. Falls back to
     the brand accent when a customer has not set one. */
  background: var(--ft-accent, ${BRAND_ACCENT});
  color: var(--ft-accent-text, ${BRAND_ACCENT_TEXT});
  border-radius: var(--ft-radius, 0px);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

/*
 * A statement about what just happened, not a failure.
 *
 * Separate from \`.ft-hint\`, which is the description's validation error and is
 * painted with the danger token. Clearing marks after a retake is expected and
 * explained; painting it red tells a reporter something went wrong when nothing
 * did. Guarded in \`prohibited-values.test.ts\`, which no denylist could do:
 * the danger token is legitimate elsewhere, so only a rule about THIS selector
 * can catch it.
 */
.ft-capture-notice {
  margin-top: 4px;
  font-size: 12px;
  color: var(--ft-muted);
}

.ft-capture-hint {
  margin: 0;
  font-size: 12px;
  color: var(--ft-muted);
}

/*
 * The screenshot slot while the capture behind the open modal is still being
 * taken (I22657).
 *
 * The busy mark is a small square that turns, Sightlines' Compose Now block
 * from e9eca08: 11px, a hairline frame with one accent edge, one linear turn
 * every 0.7s. The design language allows exactly this rotation and no other
 * (ADR 0035): a square, beside words that say what is happening, never a
 * circle and never standing in for the words. Square corners are not set
 * here; they are the absence of a radius, and \`prohibited-values.test.ts\`
 * fails a 50% radius anywhere in this package. Reduced motion holds it still.
 */
/* Close to the height a typical capture lands at, so the dialog moves as
   little as possible when the picture replaces it. White, as Sightlines drew
   it: the square's hairline frame disappears on the mist fill. */
.ft-capture-pending {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 240px;
  padding: 16px;
  border: 1px solid var(--ft-line);
  border-radius: var(--ft-radius, 0px);
  background: var(--ft-surface);
  font-size: 13px;
  color: var(--ft-text);
}

.ft-busy-square {
  flex: none;
  width: 11px;
  height: 11px;
  border: 2px solid var(--ft-line);
  border-top-color: var(--ft-accent, ${BRAND_ACCENT});
  animation: ft-busy-turn 0.7s linear infinite;
}

@keyframes ft-busy-turn {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ft-busy-square { animation: none; }
}

.ft-capture-pin-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ft-capture-pin-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ft-capture-pin-number {
  flex: 0 0 auto;
  min-width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* The same paint as \`.ft-capture-pin\`, and that is the requirement rather
     than a preference: this number labels that marker, and a 1 in one colour
     beside a 1 in another reads as two different things. */
  background: var(--ft-accent, ${BRAND_ACCENT});
  color: var(--ft-accent-text, ${BRAND_ACCENT_TEXT});
  border-radius: var(--ft-radius, 0px);
  font-size: 11px;
  font-weight: 700;
}

.ft-capture-pin-remove {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--ft-muted);
  border-radius: var(--ft-radius, 0px);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.ft-capture-pin-remove:hover:not(:disabled) {
  background: var(--ft-recess);
  color: var(--ft-text);
}

/* A host's checkbox (I22658), such as Works' consent to send app logs. */
.ft-consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ft-text);
  cursor: pointer;
}

.ft-consent input {
  flex: none;
  margin: 2px 0 0;
  accent-color: var(--ft-accent, ${BRAND_ACCENT});
}

.ft-consent input:focus-visible {
  outline: 2px solid var(--ft-accent, ${BRAND_ACCENT});
  outline-offset: 2px;
}

/* Screen recording (I19222, ADR 0030). */
.ft-recording {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ft-recording-confirm {
  border: 1px solid var(--ft-line);
  border-radius: var(--ft-radius, 0px);
  background: var(--ft-recess);
  padding: 10px 12px;
}

.ft-recording-explainer {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--ft-text);
}

.ft-recording-confirm-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.ft-recording-start,
.ft-recording-stop {
  border: none;
  background: var(--ft-accent, ${BRAND_ACCENT});
  color: var(--ft-accent-text, ${BRAND_ACCENT_TEXT});
  border-radius: var(--ft-radius, 0px);
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ft-recording-status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ft-recording-time {
  font-size: 13px;
  font-weight: 600;
  color: var(--ft-text);
  font-variant-numeric: tabular-nums;
}

/*
 * The budget meter: ten squares, filling left to right.
 *
 * Not a ring, and not an audio level. The circle is banned outright, and the
 * level is the wrong measurement: how loud the reporter is speaking is not
 * something they can act on, and how much of their thirty seconds is left is.
 */
.ft-recording-meter {
  display: inline-flex;
  gap: 3px;
}

.ft-recording-segment {
  width: 8px;
  height: 14px;
  background: var(--ft-line);
}

.ft-recording-segment-on {
  /* Elapsed budget, not an alarm. A meter three seconds into thirty is not
     warning about anything, and ADR 0003 gives red to something that broke. */
  background: var(--ft-accent, ${BRAND_ACCENT});
}
`;
